/* eslint no-console: off */
import { fromFlux } from '@influxdata/giraffe';
import { useAppSelector } from 'app/config/store';
import { TimeRange } from 'app/entities/dashboard/timeRange';
import { DataQuery } from 'app/entities/dashboard/utils/utils';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

const RAW_API_URL = '/influxmanager/api/v2/transform';
const SERVER_API_URL = 'https://preprodrainspot.com';
const GRAPH_UPDATE_DELAY = 1800000;

const cleanData = (data: string) =>
  data
    .replace(/^\s*\n/gm, '')
    .replace(/data:/gi, '')
    .trim();

export const useGraphDelta = ({
  dataQuery,
  timeRange,
  updateOnce = true,
  enabled = true,
  displayType,
}: {
  dataQuery: DataQuery;
  timeRange: TimeRange;
  updateOnce?: boolean;
  enabled?: boolean;
  displayType: string;
}) => {
  const applicationUser = useAppSelector(state => state.authentication?.internalUser);

  const [isFetched, setIsFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [rawData, setRawData] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const intervalRef = useRef<number | undefined>();
  const activeRequestRef = useRef<AbortController | null>(null); // ✅ single controller tracker

  const getGraphPayload = () => {
    const metric = dataQuery.metric;
    const operation = dataQuery.operation;
    const deviceStatus = dataQuery.deviceStatus;
    const city = dataQuery?.city?.label?.split(',')[0]?.trim();
    const state = dataQuery?.city?.label?.split(',')[2]?.trim();

    return {
      displayType,
      tenantName: dataQuery.tenantName
        ? dataQuery?.tenantName?.toLocaleLowerCase()
        : applicationUser?.organization?.name?.toLocaleLowerCase(),
      namespace: dataQuery.namespace ? dataQuery.namespace?.toLocaleLowerCase() : applicationUser?.organization?.name?.toLocaleLowerCase(),
      org: dataQuery.org ? Number(dataQuery.org) : applicationUser?.organization?.id,
      time: timeRange ?? '-7d',
      measurement: 'anomaly',
      descriptors: metric,
      operation,
      queries: {
        rs_serial: Array.isArray(dataQuery?.queries) ? [...dataQuery.queries] : [],
        city: city ? [city] : [],
        state: state ? [state] : [],
        deviceState: ['ONLINE', 'OFFLINE'].includes(deviceStatus) ? [deviceStatus] : [],
      },
    };
  };

  const getTimeSeries = async () => {
    if (!dataQuery) return;

    // ✅ Cancel any previous ongoing request before starting a new one
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    const controller = new AbortController();
    activeRequestRef.current = controller;

    try {
      const anomalyPayload = getGraphPayload();
      setIsLoading(true);

      // --- Non-streaming for map ---
      if (displayType === 'map') {
        const response = await axios.post(`${RAW_API_URL}`, anomalyPayload, {
          signal: controller.signal, // pass abort signal to axios too
        });

        setIsFetched(true);
        setIsLoading(false);

        if (response?.data) {
          const formattedData = cleanData(response.data);
          const parsedData = fromFlux(formattedData);
          setRawData(formattedData);
          setData(parsedData);
        }
        return;
      }

      // --- Streaming for charts ---
      const authToken = JSON.parse(
        localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken') || 'null',
      );

      const response = await fetch(`${SERVER_API_URL}${RAW_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(anomalyPayload),
        signal: controller.signal,
      });

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        let hasValidData = false;
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            if (!hasValidData) {
              setRawData(null);
              setData(null);
            }
            setIsFetched(true);
            setIsLoading(false);
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          try {
            const formattedData = cleanData(buffer);
            const parsedData = fromFlux(formattedData);

            const hasColumns = parsedData?.table && Object.keys((parsedData.table as any).columns).length > 0;

            if (hasColumns) {
              setRawData(formattedData);
              setData(parsedData);
              setIsFetched(true);
              setIsLoading(false);
              hasValidData = true;
            } else {
              console.debug('Skipping invalid/empty chunk');
            }
          } catch {
            console.debug('Skipping invalid/empty chunk');
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return; // expected cancellation
        throw err;
      } finally {
        reader.releaseLock?.();
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return; // ignore abort
      setError(err);
      setData(null);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;

    getTimeSeries();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!updateOnce) {
      intervalRef.current = window.setInterval(() => {
        getTimeSeries();
      }, GRAPH_UPDATE_DELAY);
    }

    return () => {
      // Clean up everything on unmount
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (activeRequestRef.current) {
        activeRequestRef.current.abort(); // cancel active API call
      }
    };
  }, [JSON.stringify(dataQuery), timeRange, applicationUser, updateOnce, enabled]);

  return { data, error, rawData, isFetched, isLoading };
};
